import React, { useState, useEffect } from 'react';
import { Shield, Lock, Eye, FileText, UserCheck, Bell, Mail } from 'lucide-react';

const PrivacyPolicyPage = ({ onNavigate }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleNavigation = (page) => {
        if (onNavigate) {
            onNavigate(page);
        }
        setIsMenuOpen(false);
    };

    const sections = [
        {
            icon: <FileText className="w-6 h-6 text-teal-600" />,
            title: "Information We Collect",
            content: "We collect personal information such as your name, email address, phone number, and academic details when you fill out forms on our website, apply for admission, or contact us. We also collect non-personal information like your browser type, IP address, and pages visited to improve our website's performance."
        },
        {
            icon: <Eye className="w-6 h-6 text-teal-600" />,
            title: "How We Use Your Information",
            content: (
                <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
                    <li>To process your admission application and provide educational services.</li>
                    <li>To communicate with you regarding updates, events, and academic schedules.</li>
                    <li>To improve our website and user experience.</li>
                    <li>To comply with legal obligations and regulatory requirements.</li>
                </ul>
            )
        },
        {
            icon: <Lock className="w-6 h-6 text-teal-600" />,
            title: "Data Security",
            content: "We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, please note that no method of transmission over the internet is 100% secure."
        },
        {
            icon: <Shield className="w-6 h-6 text-teal-600" />,
            title: "Cookies",
            content: "Our website uses cookies to enhance your browsing experience. You can choose to disable cookies through your browser settings, but this may limit some functionalities of our website."
        },
        {
            icon: <UserCheck className="w-6 h-6 text-teal-600" />,
            title: "Third-Party Disclosure",
            content: "We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties unless we provide users with advance notice. This does not include website hosting partners and other parties who assist us in operating our website, conducting our business, or serving our users."
        },
        {
            icon: <Bell className="w-6 h-6 text-teal-600" />,
            title: "Your Rights",
            content: "You have the right to access, update, or delete the information we have on you. Whenever made possible, you can access, update, or request deletion of your personal information directly within your account settings section."
        }
    ];

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', fontFamily: '"Inter", sans-serif' }}>
            {/* Header - Restored Original */}
            <header style={{
                backgroundColor: '#ffffff',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                position: 'sticky',
                top: 0,
                zIndex: 50
            }}>
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '0 1rem'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '1rem 0'
                    }}>
                        {/* Logo */}
                        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleNavigation('home')}>
                            <img
                                src="/logo.png"
                                alt="Swami Vivekananda University Logo"
                                style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                            />
                            <div style={{ marginLeft: '12px' }}>
                                <h1 style={{
                                    fontSize: isMobile ? '16px' : '20px',
                                    fontWeight: 'bold',
                                    color: '#1f2937',
                                    margin: 0
                                }}>Swami Vivekananda</h1>
                                <p style={{
                                    fontSize: '14px',
                                    color: '#0f766e',
                                    margin: 0
                                }}>University</p>
                            </div>
                        </div>

                        {/* Desktop Navigation */}
                        {!isMobile && (
                            <nav style={{
                                display: 'flex',
                                gap: '2rem'
                            }}>
                                <button onClick={() => handleNavigation('home')} style={{
                                    color: '#374151',
                                    fontWeight: '500',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'color 0.3s',
                                    position: 'relative',
                                    padding: '0.5rem 0',
                                    fontSize: '16px'
                                }}
                                    onMouseEnter={(e) => e.target.style.color = '#0f766e'}
                                    onMouseLeave={(e) => e.target.style.color = '#374151'}>
                                    Home
                                </button>
                                <button onClick={() => handleNavigation('about')} style={{
                                    color: '#374151',
                                    fontWeight: '500',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'color 0.3s',
                                    position: 'relative',
                                    padding: '0.5rem 0',
                                    fontSize: '16px'
                                }}
                                    onMouseEnter={(e) => e.target.style.color = '#0f766e'}
                                    onMouseLeave={(e) => e.target.style.color = '#374151'}>
                                    About
                                </button>
                                <button onClick={() => handleNavigation('contact')} style={{
                                    color: '#374151',
                                    fontWeight: '500',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'color 0.3s',
                                    position: 'relative',
                                    padding: '0.5rem 0',
                                    fontSize: '16px'
                                }}
                                    onMouseEnter={(e) => e.target.style.color = '#0f766e'}
                                    onMouseLeave={(e) => e.target.style.color = '#374151'}>
                                    Contact
                                </button>
                            </nav>
                        )}

                        {/* Desktop Login Button */}
                        {!isMobile && (
                            <button onClick={() => handleNavigation('login')} style={{
                                backgroundColor: '#0f766e',
                                color: 'white',
                                padding: '10px 24px',
                                borderRadius: '8px',
                                border: 'none',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 4px rgba(15, 118, 110, 0.2)'
                            }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#0d9488';
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 4px 8px rgba(15, 118, 110, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#0f766e';
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 2px 4px rgba(15, 118, 110, 0.2)';
                                }}>
                                Portal Login
                            </button>
                        )}

                        {/* Mobile Hamburger Menu */}
                        {isMobile && (
                            <button
                                onClick={toggleMenu}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    zIndex: 1001
                                }}
                            >
                                <div style={{
                                    width: '25px',
                                    height: '3px',
                                    backgroundColor: '#374151',
                                    borderRadius: '3px',
                                    position: 'absolute',
                                    top: isMenuOpen ? '50%' : '30%',
                                    left: '50%',
                                    transform: `translate(-50%, -50%) ${isMenuOpen ? 'rotate(45deg)' : 'rotate(0)'}`,
                                    transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                                }}></div>
                                <div style={{
                                    width: '25px',
                                    height: '3px',
                                    backgroundColor: '#374151',
                                    borderRadius: '3px',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    opacity: isMenuOpen ? 0 : 1,
                                    transition: 'all 0.3s ease'
                                }}></div>
                                <div style={{
                                    width: '25px',
                                    height: '3px',
                                    backgroundColor: '#374151',
                                    borderRadius: '3px',
                                    position: 'absolute',
                                    top: isMenuOpen ? '50%' : '70%',
                                    left: '50%',
                                    transform: `translate(-50%, -50%) ${isMenuOpen ? 'rotate(-45deg)' : 'rotate(0)'}`,
                                    transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                                }}></div>
                            </button>
                        )}
                    </div>
                </div>

                {/* Mobile Menu Overlay */}
                {isMobile && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: isMenuOpen ? '20vw' : '0',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        zIndex: 998,
                        opacity: isMenuOpen ? 1 : 0,
                        visibility: isMenuOpen ? 'visible' : 'hidden',
                        transition: 'all 0.3s ease',
                        backdropFilter: 'blur(3px)'
                    }} onClick={toggleMenu}></div>
                )}

                {/* Mobile Slide Menu */}
                {isMobile && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        right: isMenuOpen ? '0' : '-100%',
                        width: '80vw',
                        maxWidth: '300px',
                        height: '100vh',
                        backgroundColor: '#ffffff',
                        boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.2)',
                        transition: 'right 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                        zIndex: 999,
                        padding: '6rem 2rem 2rem 2rem',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                    }}>
                        {/* Menu Items */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem'
                        }}>
                            {[
                                { name: 'Home', page: 'home' },
                                { name: 'About', page: 'about' },
                                { name: 'Contact', page: 'contact' }
                            ].map((item, index) => (
                                <button
                                    key={item.name}
                                    onClick={() => handleNavigation(item.page)}
                                    style={{
                                        color: '#374151',
                                        fontWeight: '600',
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '18px',
                                        padding: '16px 20px',
                                        borderRadius: '12px',
                                        transition: 'all 0.3s ease',
                                        display: 'block',
                                        position: 'relative',
                                        transform: isMenuOpen ? 'translateX(0)' : 'translateX(50px)',
                                        opacity: isMenuOpen ? 1 : 0,
                                        transitionDelay: `${index * 0.1 + 0.2}s`,
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        width: '100%'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.color = '#0f766e';
                                        e.target.style.backgroundColor = '#f0fdfa';
                                        e.target.style.transform = 'translateX(10px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.color = '#374151';
                                        e.target.style.backgroundColor = 'transparent';
                                        e.target.style.transform = 'translateX(0)';
                                    }}
                                >
                                    {item.name}
                                    <span style={{
                                        position: 'absolute',
                                        right: '20px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        fontSize: '20px',
                                        opacity: 0.5
                                    }}>→</span>
                                </button>
                            ))}

                            {/* Portal Login Button with Green Container */}
                            <button
                                onClick={() => handleNavigation('login')}
                                style={{
                                    color: 'white',
                                    fontWeight: '600',
                                    backgroundColor: '#0f766e',
                                    border: 'none',
                                    fontSize: '18px',
                                    padding: '16px 20px',
                                    borderRadius: '12px',
                                    transition: 'all 0.3s ease',
                                    display: 'block',
                                    position: 'relative',
                                    transform: isMenuOpen ? 'translateX(0)' : 'translateX(50px)',
                                    opacity: isMenuOpen ? 1 : 0,
                                    transitionDelay: `${3 * 0.1 + 0.2}s`,
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    width: '100%',
                                    boxShadow: '0 4px 12px rgba(15, 118, 110, 0.3)',
                                    marginTop: '8px'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#0d9488';
                                    e.target.style.transform = 'translateX(10px)';
                                    e.target.style.boxShadow = '0 6px 16px rgba(15, 118, 110, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#0f766e';
                                    e.target.style.transform = 'translateX(0)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(15, 118, 110, 0.3)';
                                }}
                            >
                                Portal Login
                                <span style={{
                                    position: 'absolute',
                                    right: '20px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    fontSize: '20px',
                                    opacity: 0.8
                                }}>→</span>
                            </button>
                        </div>
                    </div>
                )}
            </header>

            {/* Hero Section */}
            <div style={{
                background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #115e59 100%)',
                padding: isMobile ? '4rem 1.5rem' : '6rem 2rem',
                color: 'white',
                textAlign: 'center',
                borderBottomLeftRadius: '50% 10%',
                borderBottomRightRadius: '50% 10%',
                marginBottom: '4rem',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: 'radial-gradient(circle at 20% 150%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% -50%, rgba(255,255,255,0.15) 0%, transparent 50%)',
                    pointerEvents: 'none'
                }}></div>

                <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        padding: '8px 16px',
                        borderRadius: '100px',
                        backdropFilter: 'blur(10px)',
                        marginBottom: '1.5rem',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        <Shield size={16} className="text-teal-200" />
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#ccfbf1' }}>Your Privacy Matters</span>
                    </div>
                    <h1 style={{
                        fontSize: isMobile ? '2.5rem' : '4rem',
                        fontWeight: '800',
                        marginBottom: '1.5rem',
                        lineHeight: '1.1',
                        letterSpacing: '-1px',
                        textShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                        Privacy Policy
                    </h1>
                    <p style={{
                        fontSize: isMobile ? '1.1rem' : '1.25rem',
                        opacity: 0.9,
                        maxWidth: '600px',
                        margin: '0 auto',
                        lineHeight: '1.6',
                        color: '#ccfbf1'
                    }}>
                        We are committed to protecting your personal information and your right to privacy.
                    </p>
                </div>
            </div>

            {/* Content Section */}
            <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 1.5rem 6rem 1.5rem' }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(450px, 1fr))',
                    gap: '2rem'
                }}>
                    {sections.map((section, index) => (
                        <div key={index} style={{
                            backgroundColor: 'white',
                            borderRadius: '24px',
                            padding: '2.5rem',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                            border: '1px solid #f3f4f6',
                            transition: 'all 0.3s ease',
                            cursor: 'default'
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.01)';
                                e.currentTarget.style.borderColor = '#ccfbf1';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)';
                                e.currentTarget.style.borderColor = '#f3f4f6';
                            }}
                        >
                            <div style={{
                                width: '48px',
                                height: '48px',
                                backgroundColor: '#f0fdfa',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '1.5rem',
                                color: '#0f766e'
                            }}>
                                {React.cloneElement(section.icon, { size: 24 })}
                            </div>
                            <h2 style={{
                                fontSize: '1.5rem',
                                fontWeight: '700',
                                color: '#111827',
                                marginBottom: '1rem',
                                letterSpacing: '-0.025em'
                            }}>
                                {section.title}
                            </h2>
                            <div style={{
                                fontSize: '1rem',
                                lineHeight: '1.75',
                                color: '#4b5563'
                            }}>
                                {section.content}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Contact Section */}
                <div style={{
                    marginTop: '4rem',
                    backgroundColor: '#111827',
                    borderRadius: '32px',
                    padding: isMobile ? '3rem 1.5rem' : '4rem',
                    textAlign: 'center',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(45deg, rgba(15, 118, 110, 0.1) 0%, transparent 100%)',
                        pointerEvents: 'none'
                    }}></div>

                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 2rem auto',
                            backdropFilter: 'blur(10px)'
                        }}>
                            <Mail size={32} color="#5eead4" />
                        </div>
                        <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem', color: 'white' }}>Still have questions?</h2>
                        <p style={{ fontSize: '1.1rem', color: '#9ca3af', marginBottom: '2.5rem', maxWidth: '600px', margin: '0 auto 2.5rem auto' }}>
                            Our team is here to help you understand how we manage and protect your data.
                        </p>
                        <a
                            href="mailto:info@swamivivekanandauniversity.ac.in"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '12px',
                                backgroundColor: '#5eead4',
                                color: '#134e4a',
                                padding: '16px 32px',
                                borderRadius: '16px',
                                fontWeight: '600',
                                textDecoration: 'none',
                                transition: 'all 0.3s ease',
                                fontSize: '1.1rem'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.backgroundColor = '#2dd4bf';
                                e.currentTarget.style.boxShadow = '0 8px 20px rgba(94, 234, 212, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.backgroundColor = '#5eead4';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <Mail size={20} />
                            Contact Privacy Team
                        </a>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer style={{ backgroundColor: '#ffffff', borderTop: '1px solid #f3f4f6', paddingTop: '4rem', paddingBottom: '2rem' }}>
                <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem' }}>
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'center' : 'flex-start', gap: '2rem', marginBottom: '4rem' }}>
                        <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '20px'
                                }}>
                                    SV
                                </div>
                                <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1f2937' }}>Swami Vivekananda University</span>
                            </div>
                            <p style={{ color: '#6b7280', maxWidth: '300px', lineHeight: '1.6' }}>
                                Empowering minds and building futures through quality education since 2019.
                            </p>
                        </div>

                        <div style={{ display: 'flex', gap: '4rem', flexDirection: isMobile ? 'column' : 'row', textAlign: isMobile ? 'center' : 'left' }}>
                            <div>
                                <h4 style={{ fontWeight: '700', color: '#111827', marginBottom: '1.5rem' }}>Quick Links</h4>
                                <ul style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: 0, listStyle: 'none' }}>
                                    {['Admissions', 'Academics', 'Research', 'Campus Life'].map(item => (
                                        <li key={item}><a href="#" style={{ color: '#6b7280', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color = '#0f766e'} onMouseLeave={e => e.target.style.color = '#6b7280'}>{item}</a></li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <h4 style={{ fontWeight: '700', color: '#111827', marginBottom: '1.5rem' }}>Legal</h4>
                                <ul style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: 0, listStyle: 'none' }}>
                                    {[
                                        { name: 'Privacy Policy', action: () => handleNavigation('privacypolicy') },
                                        { name: 'Terms of Service', action: () => { } },
                                        { name: 'Cookie Policy', action: () => { } }
                                    ].map(item => (
                                        <li key={item.name}>
                                            <button
                                                onClick={item.action}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#6b7280',
                                                    cursor: 'pointer',
                                                    padding: 0,
                                                    fontSize: '1rem',
                                                    fontFamily: 'inherit'
                                                }}
                                                onMouseEnter={e => e.target.style.color = '#0f766e'}
                                                onMouseLeave={e => e.target.style.color = '#6b7280'}
                                            >
                                                {item.name}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        borderTop: '1px solid #f3f4f6',
                        paddingTop: '2rem',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '1rem',
                        color: '#9ca3af',
                        fontSize: '0.875rem'
                    }}>
                        <p>&copy; {new Date().getFullYear()} Swami Vivekananda University. All rights reserved.</p>
                        <p>UGC Recognized Private University</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PrivacyPolicyPage;
